import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  message} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { observer } from 'mobx-react';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';

// Convert UTC to PST (UTC-8)
const toPST = (utcTime: string | null, format: string = 'MM-DD HH:mm') => {
  if (!utcTime) return '-';
  return moment.utc(utcTime).utcOffset(-8).format(format) + ' PST';
};
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Component from '../../../../shared/components';
import { videoSyncMonitorService } from '../service/videoSyncMonitorService';
import {
  IngestSource,
  IngestSourceDisplay,
  ServiceName,
  ServiceNameDisplay,
  VideoSyncStats,
  VideoSyncStatus,
  VideoSyncStatusColor,
  VideoSyncStatusDisplay,
  VideoSyncStatusIcon,
  VideoSyncTracker,
} from '../types/videoSyncMonitorType';
import './VideoSyncMonitorList.css';

const VideoSyncMonitorList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    page_size: '20',
  });

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<VideoSyncStats | null>(null);
  const [data, setData] = useState<VideoSyncTracker[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [retryingId, setRetryingId] = useState<number | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '');
  const [sourceFilter, setSourceFilter] = useState<string>(searchParams.get('ingest_source') || '');
  const [serviceFilter, setServiceFilter] = useState<string>(
    searchParams.get('service_name') || ''
  );

  const fetchStats = useCallback(async () => {
    try {
      const result = await videoSyncMonitorService.getStats();
      setStats(result);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await videoSyncMonitorService.fetch(searchParams);
      setData(result.results);
      setTotal(result.count);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRetry = async (id: number) => {
    setRetryingId(id);
    try {
      await videoSyncMonitorService.retry(id);
      message.success('Set as recheck target.');
      fetchData();
      fetchStats();
    } catch (error) {
      console.error('Failed to retry:', error);
    } finally {
      setRetryingId(null);
    }
  };

  const handleBulkRetry = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select items to retry.');
      return;
    }
    try {
      const result = await videoSyncMonitorService.bulkRetry(selectedRowKeys as number[]);
      message.success(result.message);
      setSelectedRowKeys([]);
      fetchData();
      fetchStats();
    } catch (error) {
      console.error('Failed to bulk retry:', error);
    }
  };

  const handleFilterChange = () => {
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');

    if (statusFilter) {
      params.set('status', statusFilter);
    } else {
      params.delete('status');
    }

    if (sourceFilter) {
      params.set('ingest_source', sourceFilter);
    } else {
      params.delete('ingest_source');
    }

    if (serviceFilter) {
      params.set('service_name', serviceFilter);
    } else {
      params.delete('service_name');
    }

    setSearchParams(params);
  };

  const handleResetFilter = () => {
    setStatusFilter('');
    setSourceFilter('');
    setServiceFilter('');
    setSearchParams({
      page: '1',
      page_size: '20',
    });
  };

  const getStatusIcon = (status: VideoSyncStatus) => {
    const icons = {
      pending: <ClockCircleOutlined />,
      synced: <CheckCircleOutlined />,
      failed: <CloseCircleOutlined />,
      alert_sent: <ExclamationCircleOutlined />,
    };
    return icons[status];
  };

  const columns: ColumnsType<VideoSyncTracker> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      sorter: true,
    },
    {
      title: 'Content',
      dataIndex: 'content_title',
      key: 'content_title',
      width: 200,
      render: (text: string, record: VideoSyncTracker) => (
        <div>
          <a
            href={`/episode/edit/${record.content_id}`}
            target="_blank"
            rel="noreferrer"
            style={{ fontWeight: 500 }}
          >
            {text || `Content #${record.content_id}`}
          </a>
          <div style={{ fontSize: 12, color: '#888' }}>Video ID: {record.video_id}</div>
        </div>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'ingest_source',
      key: 'ingest_source',
      width: 100,
      render: (source: IngestSource) => (
        <Tag color={source === 'kcp' ? 'blue' : 'purple'}>{IngestSourceDisplay[source]}</Tag>
      ),
    },
    {
      title: 'Service',
      dataIndex: 'service_name',
      key: 'service_name',
      width: 80,
      render: (service: ServiceName) => (
        <Tag color={service === 'odk' ? 'cyan' : 'geekblue'}>{ServiceNameDisplay[service]}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: VideoSyncStatus) => (
        <Tag icon={getStatusIcon(status)} color={VideoSyncStatusColor[status]}>
          {VideoSyncStatusIcon[status]} {VideoSyncStatusDisplay[status]}
        </Tag>
      ),
    },
    {
      title: 'Presets',
      key: 'presets',
      width: 150,
      render: (_: unknown, record: VideoSyncTracker) => {
        const expected = record.expected_presets?.length || 0;
        const found = record.found_presets?.length || 0;
        const missing = record.missing_presets?.length || 0;

        return (
          <Tooltip
            title={
              <div>
                <div>Expected: {record.expected_presets?.join(', ') || '-'}</div>
                <div>Found: {record.found_presets?.join(', ') || '-'}</div>
                <div>Missing: {record.missing_presets?.join(', ') || '-'}</div>
              </div>
            }
          >
            <Space size={4}>
              <Tag color="blue">
                {found}/{expected}
              </Tag>
              {missing > 0 && <Tag color="red">-{missing}</Tag>}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Check Count',
      dataIndex: 'check_count',
      key: 'check_count',
      width: 90,
      align: 'center',
    },
    {
      title: 'Created (PST)',
      dataIndex: 'created',
      key: 'created',
      width: 140,
      render: (text: string) => toPST(text),
    },
    {
      title: 'Last Checked (PST)',
      dataIndex: 'last_checked_at',
      key: 'last_checked_at',
      width: 140,
      render: (text: string | null) => toPST(text),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_: unknown, record: VideoSyncTracker) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => navigate(`/video-sync-monitor/detail/${record.id}`)}
            />
          </Tooltip>
          {['failed', 'alert_sent'].includes(record.status) && (
            <Tooltip title="Retry">
              <Button
                icon={<ReloadOutlined spin={retryingId === record.id} />}
                size="small"
                type="primary"
                onClick={() => handleRetry(record.id)}
                loading={retryingId === record.id}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination: any) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', pagination.current.toString());
    params.set('page_size', pagination.pageSize.toString());
    setSearchParams(params);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    getCheckboxProps: (record: VideoSyncTracker) => ({
      disabled: !['failed', 'alert_sent'].includes(record.status),
    }),
  };

  return (
    <Component.BasePageComponent>
      <div className="video-sync-monitor-container">
        {/* Page Title */}
        <h1 className="page-title">KCP/Mediahold Sync Status</h1>

        {/* Page Description */}
        <div className="page-description">
          <p>
            Monitors whether videos created during MediaHold/KCP ingest are properly synchronized
            to ODK Episodes.
            <br />
            If a preset is missing or there is a URL mismatch, the status will be shown as <strong>Failed</strong> and a notification
            will be sent.
          </p>
        </div>

        {/* Stats Cards */}
        <Row gutter={[16, 16]} className="stats-row">
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="stat-card stat-card-total">
              <Statistic
                title="Total"
                value={stats?.total || 0}
                valueStyle={{ color: '#1890ff', fontSize: 24 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="stat-card stat-card-pending">
              <Statistic
                title="Pending"
                value={stats?.pending || 0}
                suffix={
                  stats?.total ? `(${((stats.pending / stats.total) * 100).toFixed(1)}%)` : ''
                }
                valueStyle={{ color: '#faad14', fontSize: 24 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="stat-card stat-card-synced">
              <Statistic
                title="Synced"
                value={stats?.synced || 0}
                suffix={stats?.total ? `(${((stats.synced / stats.total) * 100).toFixed(1)}%)` : ''}
                valueStyle={{ color: '#52c41a', fontSize: 24 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="stat-card stat-card-failed">
              <Statistic
                title="Failed"
                value={(stats?.failed || 0) + (stats?.alert_sent || 0)}
                suffix={
                  stats?.total
                    ? `(${(((stats.failed + stats.alert_sent) / stats.total) * 100).toFixed(1)}%)`
                    : ''
                }
                valueStyle={{ color: '#ff4d4f', fontSize: 24 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" className="stat-card stat-card-info">
              <Statistic
                title="Today"
                value={stats?.today?.total || 0}
                valueStyle={{ fontSize: 24 }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card size="small" className="filter-card">
          <Row gutter={[16, 16]} align="middle">
            <Col>
              <Select
                placeholder="Status"
                allowClear
                style={{ width: 140 }}
                value={statusFilter || undefined}
                onChange={value => setStatusFilter(value || '')}
              >
                <Select.Option value="pending">Pending</Select.Option>
                <Select.Option value="synced">Synced</Select.Option>
                <Select.Option value="failed">Failed</Select.Option>
                <Select.Option value="alert_sent">Alert Sent</Select.Option>
              </Select>
            </Col>
            <Col>
              <Select
                placeholder="Source"
                allowClear
                style={{ width: 140 }}
                value={sourceFilter || undefined}
                onChange={value => setSourceFilter(value || '')}
              >
                <Select.Option value="kcp">KCP</Select.Option>
                <Select.Option value="mediahold">MediaHold</Select.Option>
              </Select>
            </Col>
            <Col>
              <Select
                placeholder="Service"
                allowClear
                style={{ width: 120 }}
                value={serviceFilter || undefined}
                onChange={value => setServiceFilter(value || '')}
              >
                <Select.Option value="odk">ODK</Select.Option>
                <Select.Option value="odc">ODC</Select.Option>
              </Select>
            </Col>
            <Col>
              <Space>
                <Button type="primary" onClick={handleFilterChange}>
                  Filter
                </Button>
                <Button onClick={handleResetFilter}>Reset</Button>
              </Space>
            </Col>
            <Col flex="auto" style={{ textAlign: 'right' }}>
              <Space>
                <Button
                  icon={<SyncOutlined />}
                  onClick={() => navigate('/video-sync-monitor/test')}
                >
                  Sync Test
                </Button>
                {selectedRowKeys.length > 0 && (
                  <Button type="primary" icon={<ReloadOutlined />} onClick={handleBulkRetry}>
                    Bulk Retry ({selectedRowKeys.length})
                  </Button>
                )}
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    fetchStats();
                    fetchData();
                  }}
                >
                  Refresh
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Table */}
        <Card size="small" className="table-card">
          <Table<VideoSyncTracker>
            rowKey="id"
            columns={columns}
            dataSource={data}
            loading={loading}
            rowSelection={rowSelection}
            pagination={{
              current: Number(searchParams.get('page')) || 1,
              pageSize: Number(searchParams.get('page_size')) || 20,
              total: total,
              showSizeChanger: true,
              showTotal: total => `Total ${total} items`,
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
            size="small"
          />
        </Card>
      </div>
    </Component.BasePageComponent>
  );
};

export default observer(VideoSyncMonitorList);
